import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { orderApi, pointApi, referralApi, couponApi } from '../../lib/supabase-api';
import { Order, PointTransaction } from '../../types';
import { supabase } from '../../lib/supabase';
import { useToast } from '../ui/Toast';

// SVG Icon Components
const UserIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const ShoppingBagIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
  </svg>
);

const CoinIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TicketIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
  </svg>
);

const GiftIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
  </svg>
);

const TruckIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
  </svg>
);

const LogoutIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
  </svg>
);

const CopyIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const SearchIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const XIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ClockIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const InfoIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const WarningIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

// Tab menu config
const tabMenu = [
  { id: 'profile', label: 'Profile', icon: UserIcon },
  { id: 'orders', label: 'Order History', icon: ShoppingBagIcon },
  { id: 'points', label: 'Reward Points', icon: CoinIcon },
  { id: 'coupons', label: 'Coupons', icon: TicketIcon },
  { id: 'referral', label: 'Refer a Friend', icon: GiftIcon },
  { id: 'tracking', label: 'Shipping Tracker', icon: TruckIcon },
];

const MyPage: React.FC = () => {
  const { user, signOut, loading, updateProfile, refreshProfile } = useAuth();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('profile');

  // Phone number masking function
  const maskPhone = (phone: string | undefined) => {
    if (!phone) return null;
    const digits = phone.replace(/[^0-9]/g, '');
    if (digits.length >= 10) {
      return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
    }
    return phone.slice(0, 3) + '****';
  };

  const [userOrders, setUserOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  const [profileData, setProfileData] = useState({
    username: user?.profile?.username || '',
    name: user?.profile?.name || '',
    phone: user?.profile?.phone || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);

  const [pointBalance, setPointBalance] = useState(0);
  const [pointHistory, setPointHistory] = useState<PointTransaction[]>([]);
  const [isLoadingPoints, setIsLoadingPoints] = useState(false);

  const [referralLink, setReferralLink] = useState<string>('');
  const [referralCode, setReferralCode] = useState<string>('');
  const [referralStats, setReferralStats] = useState({
    totalSignups: 0,
    totalConversions: 0,
    totalRewards: 0,
    recentSignups: [] as any[]
  });
  const [isLoadingReferral, setIsLoadingReferral] = useState(false);

  const [userCoupons, setUserCoupons] = useState<any[]>([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('You have been logged out.');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleCancelOrderRequest = (order: Order) => {
    setOrderToCancel(order);
    setShowCancelDialog(true);
  };

  const handleConfirmCancelOrder = async () => {
    if (!orderToCancel) return;
    setCancellingOrderId(orderToCancel.id);
    setShowCancelDialog(false);
    try {
      const result = await orderApi.cancelMyOrder(orderToCancel.id);
      if (result.success) {
        toast.success('Your order has been successfully cancelled.');
        const updatedOrders = await orderApi.getMyOrders();
        setUserOrders(updatedOrders);
      } else {
        toast.error(result.error || 'Failed to cancel the order.');
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('An error occurred while cancelling the order.');
    } finally {
      setCancellingOrderId(null);
      setOrderToCancel(null);
    }
  };

  const isCancellable = (status: string): boolean => {
    return status === 'payment_pending';
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setIsSaving(true);
    setMessage(null);
    try {
      const { error } = await updateProfile({
        username: profileData.username || undefined,
        name: profileData.name || undefined,
        phone: profileData.phone || undefined
      });
      if (error) {
        setMessage({ type: 'error', text: 'Profile update failed: ' + error.message });
      } else {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        await refreshProfile();
      }
    } catch (error) {
      console.error('Profile save failed:', error);
      setMessage({ type: 'error', text: 'An error occurred while saving your profile.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      setMessage({ type: 'error', text: 'Please enter your current password and new password.' });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New password and confirmation do not match.' });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    setIsChangingPassword(true);
    setMessage(null);
    try {
      const { error } = await supabase.auth.updateUser({ password: passwordData.newPassword });
      if (error) {
        setMessage({ type: 'error', text: 'Password change failed: ' + error.message });
      } else {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      console.error('Password change failed:', error);
      setMessage({ type: 'error', text: 'An error occurred while changing your password.' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  useEffect(() => {
    const scrollPos = sessionStorage.getItem('mypage-scroll');
    if (scrollPos) {
      window.scrollTo(0, parseInt(scrollPos));
      sessionStorage.removeItem('mypage-scroll');
    }
    return () => {
      sessionStorage.setItem('mypage-scroll', window.scrollY.toString());
    };
  }, []);

  React.useEffect(() => {
    if (user?.profile) {
      setProfileData({
        username: user.profile.username || '',
        name: user.profile.name || '',
        phone: user.profile.phone || ''
      });
    }
  }, [user?.profile?.username, user?.profile?.name, user?.profile?.phone]);

  useEffect(() => {
    if (user?.id) {
      const fetchUserOrders = async () => {
        setIsLoadingOrders(true);
        try {
          const ordersData = await orderApi.getMyOrders();
          setUserOrders(ordersData);
        } catch (error) {
          console.error('Failed to load order data:', error);
        } finally {
          setIsLoadingOrders(false);
        }
      };
      fetchUserOrders();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      const fetchPointData = async () => {
        setIsLoadingPoints(true);
        try {
          const balance = await pointApi.getBalance(user.id);
          setPointBalance(balance);
          const history = await pointApi.getTransactions(user.id);
          setPointHistory(history);
        } catch (error) {
          console.error('Failed to load points data:', error);
        } finally {
          setIsLoadingPoints(false);
        }
      };
      fetchPointData();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      const fetchReferralData = async () => {
        setIsLoadingReferral(true);
        try {
          const userName = user.profile?.name || 'User';
          const link = await referralApi.getOrCreateReferralLink(user.id, userName);
          if (link) {
            const fullUrl = `${window.location.origin}/signup?ref=${link.referral_code}`;
            setReferralLink(fullUrl);
            setReferralCode(link.referral_code);
          }
          const stats = await referralApi.getMyReferralStats(user.id);
          const transactions = await pointApi.getTransactions(user.id);
          const referralRewards = transactions
            .filter((t: any) => t.type === 'referral_reward')
            .reduce((sum: number, t: any) => sum + t.amount, 0);
          if (stats) {
            setReferralStats({
              totalSignups: stats.totalSignups || 0,
              totalConversions: stats.totalConversions || 0,
              totalRewards: referralRewards,
              recentSignups: stats.recentSignups || []
            });
          }
        } catch (error) {
          console.error('Failed to load referral data:', error);
        } finally {
          setIsLoadingReferral(false);
        }
      };
      fetchReferralData();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && activeTab === 'coupons') {
      const fetchCoupons = async () => {
        setIsLoadingCoupons(true);
        try {
          const coupons = await couponApi.getUserCoupons(user.id);
          setUserCoupons(coupons);
        } catch (error) {
          console.error('Failed to load coupons:', error);
        } finally {
          setIsLoadingCoupons(false);
        }
      };
      fetchCoupons();
    }
  }, [user?.id, activeTab]);

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-warm-500 text-sm">Please wait...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-gradient-to-br from-forest-100 to-forest-50 rounded-xl flex items-center justify-center mx-auto mb-6">
            <UserIcon className="w-10 h-10 text-gold-400" />
          </div>
          <h2 className="text-2xl font-bold text-forest-900 mb-3">Login Required</h2>
          <p className="text-warm-500 mb-8 leading-relaxed">Please log in to access<br />your account page.</p>
          <Link
            to="/login"
            className="inline-flex items-center px-8 py-3.5 bg-forest-800 hover:bg-forest-700 text-cream-100 rounded-lg border border-forest-700/50 font-medium transition-all duration-200 hover:shadow-lg hover:shadow-forest-800/25 cursor-pointer"
          >
            Log In
          </Link>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'text-emerald-700 bg-emerald-50 border border-emerald-200';
      case 'shipping': return 'text-forest-700 bg-forest-50 border border-forest-200';
      case 'payment_confirmed': return 'text-forest-700 bg-forest-50 border border-forest-200';
      case 'payment_pending': return 'text-amber-700 bg-amber-50 border border-amber-200';
      case 'cancelled': return 'text-red-700 bg-red-50 border border-red-200';
      case 'refunded': return 'text-forest-700 bg-cream-100 border border-warm-200';
      default: return 'text-forest-700 bg-cream-100 border border-warm-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered': return 'Delivered';
      case 'shipping': return 'In Transit';
      case 'payment_confirmed': return 'Payment Confirmed';
      case 'payment_pending': return 'Payment Pending';
      case 'cancelled': return 'Cancelled';
      case 'refunded': return 'Refunded';
      default: return 'Unknown';
    }
  };

  const handleTabClick = (tabId: string) => {
    if (tabId === 'tracking') {
      setActiveTab('orders');
    } else {
      setActiveTab(tabId);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100">
      {/* Breadcrumb */}
      <div className="border-b border-warm-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm text-warm-400">
            <Link to="/" className="hover:text-gold-400 transition-colors duration-200 cursor-pointer">Home</Link>
            <ChevronRightIcon />
            <span className="text-forest-800 font-medium">My Page</span>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ===== SIDEBAR ===== */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="sticky top-8 space-y-5">

              {/* Profile Card */}
              <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card overflow-hidden">
                {/* Profile header gradient */}
                <div className="h-24 bg-gradient-to-br from-forest-600 via-forest-700 to-forest-800 relative">
                  <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
                </div>

                {/* Avatar + Info */}
                <div className="px-5 pb-5 -mt-10 relative">
                  <div className="w-20 h-20 bg-cream-50 rounded-xl shadow-lg flex items-center justify-center mx-auto border-4 border-cream-50">
                    <span className="text-gold-400 text-2xl font-bold">
                      {user.profile?.name ? user.profile.name.charAt(0) : user.email?.charAt(0) || 'U'}
                    </span>
                  </div>

                  <div className="text-center mt-3">
                    <h3 className="font-bold text-forest-900 text-lg">{user.profile?.name || user.email}</h3>
                    <div className="inline-flex items-center mt-1.5 px-3 py-1 bg-forest-50 rounded-full">
                      <span className="text-xs font-semibold text-forest-800">{user.profile?.grade || 'BASIC'} Member</span>
                    </div>
                    <p className="text-xs text-warm-400 mt-2">
                      Joined {new Date(user.created_at).toLocaleDateString('en-US')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Points Summary Card */}
              <div className="bg-gradient-to-br from-forest-600 to-forest-800 rounded-xl p-5 text-cream-100 shadow-lg shadow-forest-800/20">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-forest-100 text-sm font-medium">Reward Points</span>
                  <CoinIcon className="w-5 h-5 text-forest-200" />
                </div>
                <p className="text-2xl font-bold tracking-tight">
                  ${pointBalance.toLocaleString()}<span className="text-base font-medium ml-0.5"></span>
                </p>
              </div>

              {/* Navigation */}
              <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-3">
                <nav className="space-y-1">
                  {tabMenu.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id || (tab.id === 'tracking' && activeTab === 'orders');
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabClick(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${
                          isActive
                            ? 'bg-forest-50 text-forest-800'
                            : 'text-warm-600 hover:bg-cream-100 hover:text-forest-900'
                        }`}
                      >
                        <Icon className={`w-[18px] h-[18px] ${isActive ? 'text-forest-800' : 'text-warm-400'}`} />
                        {tab.label}
                      </button>
                    );
                  })}

                  <div className="my-2 border-t border-warm-200"></div>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-all duration-200 cursor-pointer"
                  >
                    <LogoutIcon className="w-[18px] h-[18px]" />
                    Log Out
                  </button>
                </nav>
              </div>
            </div>
          </div>

          {/* ===== MAIN CONTENT ===== */}
          <div className="flex-1 min-w-0">

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-display font-bold text-forest-900">Profile Settings</h2>
                  <p className="text-warm-500 text-sm mt-1">Edit your personal information and change your password.</p>
                </div>

                {message && (
                  <div className={`flex items-center gap-3 p-4 rounded-xl text-sm ${
                    message.type === 'success'
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {message.type === 'success' ? (
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    ) : (
                      <XIcon className="w-5 h-5 flex-shrink-0" />
                    )}
                    {message.text}
                  </div>
                )}

                {/* Basic Info Card */}
                <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6 sm:p-8">
                  <h3 className="text-base font-semibold text-forest-900 mb-6">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-warm-600 mb-2">Email</label>
                      <input
                        type="email"
                        value={user.email || ''}
                        disabled
                        className="w-full px-4 py-3 border border-warm-200 rounded-xl bg-cream-100 text-warm-400 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-warm-600 mb-2">Username <span className="text-warm-400 font-normal">(optional)</span></label>
                      <input
                        type="text"
                        name="username"
                        value={profileData.username}
                        onChange={handleProfileChange}
                        placeholder="Enter your username"
                        className="w-full px-4 py-3 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-warm-600 mb-2">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={profileData.name}
                        onChange={handleProfileChange}
                        placeholder="Enter your name"
                        className="w-full px-4 py-3 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-warm-600 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={profileData.phone}
                        onChange={handleProfileChange}
                        placeholder="+1-000-000-0000"
                        className="w-full px-4 py-3 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>

                {/* Password Card */}
                <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6 sm:p-8">
                  <h3 className="text-base font-semibold text-forest-900 mb-6">Change Password</h3>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-warm-600 mb-2">Current Password</label>
                      <input
                        type="password"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter current password"
                        className="w-full px-4 py-3 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all duration-200"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-warm-600 mb-2">New Password</label>
                        <input
                          type="password"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          placeholder="At least 8 characters"
                          className="w-full px-4 py-3 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-warm-600 mb-2">Confirm Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          placeholder="Confirm new password"
                          className="w-full px-4 py-3 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all duration-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setProfileData({
                        username: user?.profile?.username || '',
                        name: user?.profile?.name || '',
                        phone: user?.profile?.phone || ''
                      });
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setMessage(null);
                    }}
                    disabled={isSaving || isChangingPassword}
                    className="px-6 py-3 border border-warm-200 text-forest-700 rounded-lg hover:bg-cream-100 transition-all duration-200 text-sm font-medium disabled:opacity-40 cursor-pointer"
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSaving || isChangingPassword}
                    className="px-6 py-3 bg-forest-800 text-cream-100 rounded-lg border border-forest-700/50 hover:bg-forest-700 transition-all duration-200 text-sm font-medium disabled:bg-warm-300 flex items-center gap-2 shadow-sm hover:shadow-md hover:shadow-forest-800/20 cursor-pointer"
                  >
                    {isSaving ? (
                      <><div className="w-4 h-4 border-2 border-cream-100 border-t-transparent rounded-full animate-spin"></div>Saving...</>
                    ) : 'Save Profile'}
                  </button>
                  {(passwordData.currentPassword || passwordData.newPassword || passwordData.confirmPassword) && (
                    <button
                      onClick={handleChangePassword}
                      disabled={isSaving || isChangingPassword}
                      className="px-6 py-3 bg-forest-700 text-cream-100 rounded-lg hover:bg-forest-800 transition-all duration-200 text-sm font-medium disabled:bg-warm-300 flex items-center gap-2 shadow-sm cursor-pointer"
                    >
                      {isChangingPassword ? (
                        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Changing...</>
                      ) : 'Change Password'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-display font-bold text-forest-900">Order History</h2>
                  <p className="text-warm-500 text-sm mt-1">Check your order status and track shipments.</p>
                </div>

                {isLoadingOrders ? (
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-16 text-center">
                    <div className="w-10 h-10 border-3 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-warm-500 text-sm">Loading order history...</p>
                  </div>
                ) : userOrders.length > 0 ? (
                  <div className="space-y-4">
                    {userOrders.map((order) => (
                      <div key={order.id} className="bg-cream-50 rounded-xl border border-warm-200 shadow-card overflow-hidden hover:border-warm-300 transition-all duration-200">
                        {/* Order Header */}
                        <div className="flex items-center justify-between px-5 sm:px-6 py-4 bg-cream-100/50 border-b border-warm-200">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-forest-900">#{order.orderNumber}</span>
                              <span className="text-xs text-warm-400">{new Date(order.createdAt).toLocaleDateString('en-US')}</span>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                        </div>

                        {/* Order Items */}
                        <div className="px-5 sm:px-6 py-4">
                          {/* PR 6: Ship-to summary so user can verify destination at a glance */}
                          {(() => {
                            const addr = typeof order.shippingAddress === 'object' && order.shippingAddress !== null
                              ? (order.shippingAddress as Record<string, unknown>)
                              : null;
                            if (!addr) return null;
                            const parts = [addr.city, addr.state, addr.country]
                              .filter((p): p is string => typeof p === 'string' && p.length > 0);
                            return parts.length > 0 ? (
                              <p className="text-xs text-warm-500 mb-3">
                                <span className="font-semibold text-warm-600">Ship to:</span> {parts.join(', ')}
                              </p>
                            ) : null;
                          })()}
                          <div className="space-y-3">
                            {order.items.map((item, index) => (
                              <div key={index} className="flex items-center gap-3">
                                {/* PR 6: product image so user recognizes order at a glance */}
                                <img
                                  src={item.image || '/images/placeholder.png'}
                                  alt={item.productName}
                                  className="w-14 h-14 object-contain rounded-lg bg-cream-100 border border-warm-200 flex-shrink-0"
                                  onError={(e) => { (e.target as HTMLImageElement).src = '/images/placeholder.png'; }}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-forest-900 truncate">{item.productName}</p>
                                  <p className="text-xs text-warm-400 mt-0.5">Qty: {item.quantity} items</p>
                                </div>
                                <p className="text-sm font-semibold text-forest-900 ml-2 flex-shrink-0">
                                  ${item.price.toLocaleString()}
                                </p>
                              </div>
                            ))}
                          </div>

                          {/* Status Info Bars */}
                          <div className="mt-4">
                            {order.status === 'cancelled' ? (
                              <div className="flex items-center gap-2.5 p-3 bg-cream-100 rounded-xl">
                                <XIcon className="w-4 h-4 text-warm-500 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-forest-700">This order has been cancelled.</p>
                                  {/* PR 6: refund message conditional on whether payment actually happened */}
                                  <p className="text-xs text-warm-400 mt-0.5">
                                    {order.paymentStatus === 'paid' || order.paymentStatus === 'refunded'
                                      ? 'Refund will be processed within 3-5 business days.'
                                      : 'No payment was made — nothing to refund.'}
                                  </p>
                                </div>
                              </div>
                            ) : order.trackingNumber ? (
                              <div className="flex items-center justify-between p-3 bg-forest-50/80 rounded-xl border border-forest-100">
                                <div>
                                  <p className="text-xs font-medium text-gold-400 mb-0.5">{order.shippingCompany || 'EMS International'}</p>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-mono font-bold text-forest-900">{order.trackingNumber}</span>
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(order.trackingNumber || '');
                                        toast.success('Tracking number copied!');
                                      }}
                                      className="text-gold-400 hover:text-gold-500 transition-colors cursor-pointer"
                                      aria-label="Copy tracking number"
                                    >
                                      <CopyIcon className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    window.open(`https://www.17track.net/en/track#nums=${encodeURIComponent(order.trackingNumber || '')}`, '_blank');
                                  }}
                                  className="px-3.5 py-2 bg-forest-800 text-cream-100 rounded-lg border border-forest-700/50 text-xs font-medium hover:bg-forest-700 transition-all duration-200 flex items-center gap-1.5 shadow-sm cursor-pointer"
                                >
                                  <SearchIcon className="w-3.5 h-3.5" />
                                  <span>Track Now</span>
                                </button>
                              </div>
                            ) : order.status === 'payment_pending' ? (
                              <div className="flex items-center gap-2.5 p-3 bg-amber-50/80 rounded-xl border border-amber-100">
                                <ClockIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-amber-800">Payment Pending</p>
                                  <p className="text-xs text-amber-600 mt-0.5">Once payment is confirmed, your order will be placed with the supplier.</p>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2.5 p-3 bg-amber-50/80 rounded-xl border border-amber-100">
                                <InfoIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-amber-800">Tracking Number Pending</p>
                                  <p className="text-xs text-amber-600 mt-0.5">A tracking number will be assigned after your order ships.</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Order Footer */}
                        <div className="px-5 sm:px-6 py-4 border-t border-warm-200 bg-cream-100/30">
                          <div className="flex items-center justify-between mb-3">
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between gap-8">
                                <span className="text-warm-400">Product Total</span>
                                <span className="text-warm-600">${order.items.reduce((sum, item) => sum + item.price, 0).toLocaleString()}</span>
                              </div>
                              {(order.shippingFee || 0) > 0 && (
                                <div className="flex justify-between gap-8">
                                  <span className="text-warm-400">Shipping</span>
                                  <span className="text-warm-600">${(order.shippingFee || 0).toLocaleString()}</span>
                                </div>
                              )}
                              {(order.shippingFee || 0) === 0 && (
                                <div className="flex justify-between gap-8">
                                  <span className="text-warm-400">Shipping</span>
                                  <span className="text-green-600">Free</span>
                                </div>
                              )}
                              {(order.discountAmount || 0) > 0 && (
                                <div className="flex justify-between gap-8">
                                  <span className="text-warm-400">Discount</span>
                                  <span className="text-gold-400">-${(order.discountAmount || 0).toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-warm-400 mb-0.5">Order Total</p>
                              <p className="text-xl font-bold text-forest-900">${order.totalAmount.toLocaleString()}</p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-3 border-t border-warm-200">
                            {/* PR 6: "View All Shipments" link removed pending /tracking page audit (P2) */}
                            <div className={`flex flex-wrap gap-2 ${order.status === 'cancelled' ? 'w-full justify-end' : 'ml-auto'}`}>
                              {isCancellable(order.status) && (
                                <button
                                  onClick={() => handleCancelOrderRequest(order)}
                                  disabled={cancellingOrderId === order.id}
                                  className="px-3.5 py-2 border border-red-200 text-red-600 rounded-lg text-xs font-medium hover:bg-red-50 transition-all duration-200 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                >
                                  {cancellingOrderId === order.id ? (
                                    <><div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>Cancelling...</>
                                  ) : (
                                    <><XIcon className="w-3.5 h-3.5" />Cancel Order</>
                                  )}
                                </button>
                              )}
                              {order.status !== 'cancelled' && (
                                <>
                                  {order.trackingNumber ? (
                                    <button
                                      onClick={() => {
                                        window.open(`https://www.17track.net/en/track#nums=${encodeURIComponent(order.trackingNumber || '')}`, '_blank');
                                      }}
                                      className="px-3.5 py-2 border border-forest-200 text-gold-400 rounded-lg text-xs font-medium hover:bg-forest-50 transition-all duration-200 flex items-center gap-1.5 cursor-pointer"
                                    >
                                      <SearchIcon className="w-3.5 h-3.5" />Track Shipment
                                    </button>
                                  ) : order.status === 'payment_pending' ? (
                                    <Link
                                      to={`/crypto-payment/${order.id}`}
                                      className="px-3.5 py-2 border border-amber-300 text-amber-700 bg-amber-50 rounded-lg text-xs font-medium hover:bg-amber-100 transition-all duration-200 flex items-center gap-1.5 cursor-pointer"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      Continue Payment
                                    </Link>
                                  ) : (
                                    <span className="px-3.5 py-2 border border-warm-200 text-warm-400 rounded-lg text-xs font-medium bg-cream-100">
                                      Preparing Shipment
                                    </span>
                                  )}
                                  {order.status === 'delivered' && (
                                    <button className="px-3.5 py-2 bg-forest-800 text-cream-100 rounded-lg border border-forest-700/50 text-xs font-medium hover:bg-forest-700 transition-all duration-200 cursor-pointer">
                                      Write Review
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-16 text-center">
                    <div className="w-16 h-16 bg-cream-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <ShoppingBagIcon className="w-8 h-8 text-warm-300" />
                    </div>
                    <p className="text-forest-800 font-medium text-lg">No order history</p>
                    <p className="text-warm-400 text-sm mt-1.5">Place your first order!</p>
                    <Link
                      to="/shop"
                      className="inline-flex items-center mt-6 px-6 py-3 bg-forest-800 text-cream-100 rounded-lg border border-forest-700/50 text-sm font-medium hover:bg-forest-700 transition-all duration-200 hover:shadow-lg hover:shadow-forest-800/20 cursor-pointer"
                    >
                      Start Shopping
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Points Tab */}
            {activeTab === 'points' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-display font-bold text-forest-900">Reward Points History</h2>
                  <p className="text-warm-500 text-sm mt-1">View your points balance and transaction history.</p>
                </div>

                {/* Points Balance Card */}
                <div className="bg-gradient-to-br from-forest-600 via-forest-700 to-forest-800 rounded-xl p-8 text-cream-100 shadow-lg shadow-forest-800/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                  <div className="relative">
                    <p className="text-forest-100 text-sm font-medium mb-2">Total Reward Points</p>
                    <p className="text-4xl font-bold tracking-tight">
                      ${pointBalance.toLocaleString()}
                    </p>
                  </div>
                </div>

                {isLoadingPoints ? (
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-16 text-center">
                    <div className="w-10 h-10 border-3 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-warm-500 text-sm">Loading...</p>
                  </div>
                ) : pointHistory.length > 0 ? (
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-warm-200">
                            <th className="px-6 py-4 text-left text-xs font-semibold text-warm-500 uppercase tracking-wider">Description</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-warm-500 uppercase tracking-wider">Points</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-warm-500 uppercase tracking-wider">Balance</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-warm-500 uppercase tracking-wider">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-cream-100">
                          {pointHistory.map((transaction: any) => (
                            <tr key={transaction.id} className="hover:bg-cream-100/50 transition-colors">
                              <td className="px-6 py-4 text-sm text-forest-800">{transaction.description}</td>
                              <td className="px-6 py-4 text-sm">
                                <span className={`font-semibold ${transaction.amount > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {transaction.amount > 0 ? '+' : ''}${transaction.amount.toLocaleString()}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm font-medium text-gold-400">
                                ${transaction.balance_after.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 text-sm text-warm-400">
                                {new Date(transaction.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric', month: '2-digit', day: '2-digit',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-16 text-center">
                    <div className="w-16 h-16 bg-cream-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <CoinIcon className="w-8 h-8 text-warm-300" />
                    </div>
                    <p className="text-forest-800 font-medium text-lg">No points history</p>
                    <p className="text-warm-400 text-sm mt-1.5">Earn points by making purchases and writing reviews!</p>
                  </div>
                )}
              </div>
            )}

            {/* Coupons Tab */}
            {activeTab === 'coupons' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-display font-bold text-forest-900">My Coupons</h2>
                  <p className="text-warm-500 text-sm mt-1">View and manage your available coupons.</p>
                </div>

                {isLoadingCoupons ? (
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-16 text-center">
                    <div className="w-10 h-10 border-3 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-warm-500 text-sm">Loading coupons...</p>
                  </div>
                ) : userCoupons.length === 0 ? (
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-16 text-center">
                    <div className="w-16 h-16 bg-cream-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <TicketIcon className="w-8 h-8 text-warm-300" />
                    </div>
                    <p className="text-forest-800 font-medium text-lg">No coupons available</p>
                    <p className="text-warm-400 text-sm mt-1.5">Get coupons through events and promotions!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {userCoupons.map((userCoupon: any) => {
                      const coupon = userCoupon.coupons;
                      const isExpired = coupon.end_date && new Date(coupon.end_date) < new Date();
                      const isUsed = userCoupon.is_used;
                      const isDisabled = isUsed || isExpired || !coupon.is_active;

                      return (
                        <div
                          key={userCoupon.id}
                          className={`bg-cream-50 rounded-xl border shadow-sm overflow-hidden transition-all duration-200 ${
                            isDisabled ? 'border-warm-200 opacity-50' : 'border-forest-100 hover:border-forest-200 hover:shadow-md'
                          }`}
                        >
                          <div className="flex">
                            {/* Coupon value side */}
                            <div className={`w-32 sm:w-40 flex-shrink-0 flex flex-col items-center justify-center p-4 ${
                              isDisabled ? 'bg-cream-100' : 'bg-gradient-to-br from-forest-600 to-forest-800'
                            }`}>
                              <span className={`text-lg sm:text-xl font-bold ${isDisabled ? 'text-warm-400' : 'text-cream-100'}`}>
                                {coupon.discount_type === 'percentage'
                                  ? `${Number(coupon.discount_value) * 100}%`
                                  : `$${Number(coupon.discount_value).toLocaleString()}`}
                              </span>
                              <span className={`text-xs mt-0.5 ${isDisabled ? 'text-warm-400' : 'text-forest-100'}`}>OFF</span>
                            </div>

                            {/* Coupon details */}
                            <div className="flex-1 p-4 flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <h4 className="font-semibold text-forest-900 text-sm">{coupon.name}</h4>
                                <p className="text-xs text-warm-400 mt-1 font-mono">{coupon.code}</p>
                                {coupon.min_amount > 0 && (
                                  <p className="text-xs text-warm-400 mt-1">Min. purchase: ${coupon.min_amount.toLocaleString()}</p>
                                )}
                                {coupon.end_date && (
                                  <p className="text-xs text-warm-400 mt-1">~ {new Date(coupon.end_date).toLocaleDateString()}</p>
                                )}
                              </div>
                              <div className="flex-shrink-0">
                                {isUsed ? (
                                  <span className="px-3 py-1.5 bg-warm-100 text-warm-500 rounded-full text-xs font-semibold">Used</span>
                                ) : isExpired ? (
                                  <span className="px-3 py-1.5 bg-red-50 text-red-500 rounded-full text-xs font-semibold">Expired</span>
                                ) : !coupon.is_active ? (
                                  <span className="px-3 py-1.5 bg-warm-100 text-warm-500 rounded-full text-xs font-semibold">Inactive</span>
                                ) : (
                                  <span className="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold border border-emerald-200">Available</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Referral Tab */}
            {activeTab === 'referral' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-display font-bold text-forest-900">Refer a Friend</h2>
                  <p className="text-warm-500 text-sm mt-1">Invite friends and earn rewards together!</p>
                </div>

                {/* Benefits Card */}
                <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card overflow-hidden">
                  <div className="bg-forest-50/60 p-6 sm:p-8">
                    <h3 className="font-bold text-forest-900 mb-5">Referral Benefits</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-cream-50 rounded-xl p-4 border border-warm-200">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-9 h-9 bg-forest-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <UserIcon className="w-4 h-4 text-gold-500" />
                          </div>
                          <span className="font-semibold text-forest-900 text-sm">When a friend signs up</span>
                        </div>
                        <p className="text-sm text-warm-600 ml-12">Friend receives a <strong className="text-gold-500">$10 coupon</strong> automatically</p>
                      </div>
                      <div className="bg-cream-50 rounded-xl p-4 border border-warm-200">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-9 h-9 bg-forest-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <GiftIcon className="w-4 h-4 text-gold-500" />
                          </div>
                          <span className="font-semibold text-forest-900 text-sm">When a friend makes first purchase</span>
                        </div>
                        <p className="text-sm text-warm-600 ml-12">You receive a <strong className="text-gold-500">$10 coupon</strong></p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Referral Link */}
                <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6 sm:p-8">
                  <h3 className="font-semibold text-forest-900 mb-4">My Referral Link</h3>
                  {isLoadingReferral ? (
                    <div className="text-center py-8">
                      <div className="w-8 h-8 border-3 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <p className="text-sm text-warm-500">Generating link...</p>
                    </div>
                  ) : referralLink ? (
                    <>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={referralLink}
                          className="flex-1 px-4 py-3 bg-cream-100 border border-warm-200 rounded-xl text-sm text-warm-600 font-mono"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(referralLink);
                            toast.success('Link copied!');
                          }}
                          className="px-5 py-3 bg-forest-800 text-cream-100 rounded-lg border border-forest-700/50 hover:bg-forest-700 font-medium whitespace-nowrap text-sm transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-forest-800/20 cursor-pointer"
                        >
                          Copy
                        </button>
                      </div>
                      <div className="mt-3 px-4 py-3 bg-forest-50 rounded-xl border border-forest-100">
                        <p className="text-sm font-medium text-forest-800">
                          My Referral Code: <span className="font-mono font-bold text-gold-400">{referralCode}</span>
                        </p>
                      </div>
                      <p className="text-xs text-warm-400 mt-3">Share this link with friends! Benefits are automatically applied when they sign up.</p>
                    </>
                  ) : (
                    <p className="text-center text-red-500 py-4 text-sm">Failed to generate link. Please refresh the page.</p>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-5 text-center">
                    <p className="text-3xl font-bold text-forest-700">{referralStats.totalSignups}</p>
                    <p className="text-sm text-warm-500 mt-1">Friends Invited</p>
                  </div>
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-5 text-center">
                    <p className="text-3xl font-bold text-forest-700">{referralStats.totalConversions}</p>
                    <p className="text-sm text-warm-500 mt-1">Purchases Made</p>
                  </div>
                  <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-5 text-center">
                    <p className="text-3xl font-bold text-gold-500">${referralStats.totalRewards.toLocaleString()}</p>
                    <p className="text-sm text-warm-500 mt-1">Rewards Earned</p>
                  </div>
                </div>

                {/* Recent Referrals */}
                <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6">
                  <h3 className="font-semibold text-forest-900 mb-4">Recent Referrals</h3>
                  {referralStats.recentSignups.length > 0 ? (
                    <div className="space-y-2">
                      {referralStats.recentSignups.map((signup: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-cream-100 rounded-xl hover:bg-warm-100/80 transition-colors">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-9 h-9 bg-forest-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <UserIcon className="w-4 h-4 text-gold-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-forest-900 truncate">
                                {signup.referee?.name || `Friend ${index + 1}`}
                              </p>
                              {signup.referee?.phone && (
                                <p className="text-xs text-warm-500 truncate">
                                  {maskPhone(signup.referee.phone)}
                                </p>
                              )}
                              <p className="text-xs text-warm-400 mt-0.5">
                                Joined {new Date(signup.created_at).toLocaleDateString('en-US')}
                              </p>
                            </div>
                          </div>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ml-2 ${
                            signup.first_purchase_at
                              ? 'bg-forest-50 text-forest-700 border border-forest-200'
                              : 'bg-cream-100 text-warm-500 border border-warm-200'
                          }`}>
                            {signup.first_purchase_at ? 'Purchased' : 'Signed Up'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 bg-cream-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <UserIcon className="w-7 h-7 text-warm-300" />
                      </div>
                      <p className="text-sm text-warm-500">No referrals yet.</p>
                      <p className="text-xs text-warm-400 mt-1">Share the link above to get started!</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Order Dialog */}
      {showCancelDialog && orderToCancel && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-cream-50 rounded-xl shadow-2xl max-w-md w-full p-6 sm:p-8">
            <div className="flex items-center justify-center w-14 h-14 mx-auto mb-5 bg-red-100 rounded-xl">
              <WarningIcon className="w-7 h-7 text-red-600" />
            </div>

            <h3 className="text-xl font-bold text-forest-900 text-center mb-2">
              Cancel this order?
            </h3>

            <div className="bg-cream-100 rounded-xl p-4 mb-6 mt-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-warm-500">Order #</span>
                  <span className="font-medium text-forest-900">#{orderToCancel.orderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-warm-500">Product</span>
                  <span className="font-medium text-forest-900 text-right max-w-[200px] truncate">
                    {orderToCancel.items.map(item => item.productName).join(', ')}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-warm-200">
                  <span className="text-warm-500">Total Amount</span>
                  <span className="font-bold text-forest-900">${orderToCancel.totalAmount.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2.5 mb-6">
              <div className="flex items-start gap-2.5 p-3 bg-red-50 rounded-xl border border-red-200">
                <WarningIcon className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700 font-medium leading-relaxed">
                  Due to the nature of international orders, refunds are not possible once a tracking number has been issued.
                </p>
              </div>
              <div className="flex items-start gap-2.5 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <InfoIcon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  Cancelled orders cannot be restored. Refunds will be processed within 3-5 business days.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelDialog(false);
                  setOrderToCancel(null);
                }}
                className="flex-1 px-4 py-3 border border-warm-200 text-forest-700 rounded-lg hover:bg-cream-100 transition-all duration-200 font-medium text-sm cursor-pointer"
              >
                No
              </button>
              <button
                onClick={handleConfirmCancelOrder}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-medium text-sm cursor-pointer"
              >
                Yes, Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPage;
